/**
 * @description
 * Client detail page showing comprehensive information about a single client,
 * including deployment status, configuration, and logs.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 * @see admin/src/frontend/pages/ClientDetail.test.tsx
 */

import { useParams, Link } from 'react-router';
import ClientUsersTab from '../components/ClientUsers/ClientUsersTab';
import { useClientData } from './ClientDetail/hooks/useClientData';
import { ClientOverview } from './ClientDetail/ClientOverview';
import { ClientConfiguration } from './ClientDetail/ClientConfiguration';
import { ClientLogs } from './ClientDetail/ClientLogs';
import { ClientAnthropic } from './ClientDetail/ClientAnthropic';
import { ClientBilling } from './ClientDetail/ClientBilling';
import { ClientApiKeys } from './ClientDetail/ClientApiKeys';

/**
 * Client detail page component
 */
export function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  
  const {
    client,
    loading,
    latestDeployment,
    deploymentsLoading,
    readiness,
    readinessLoading,
    activeTab,
    configs,
    configsLoading,
    editingKey,
    editValue,
    showSecrets,
    saving,
    deploying,
    provisioningDatabase,
    deploymentStatus,
    loadingDeploymentStatus,
    setActiveTab,
    handleInitializeDefaults,
    handleSaveConfig,
    handleDeleteConfig,
    startEdit,
    cancelEdit,
    toggleSecretVisibility,
    handleDeploy,
    handleProvisionDatabase,
    handleStartLocalDeployment,
    handleStopDeployment,
  } = useClientData(id!);

  const getReadinessColor = (readinessScore: number) => {
    if (readinessScore >= 90) return 'bg-green-100 text-green-800';
    if (readinessScore >= 70) return 'bg-yellow-100 text-yellow-800';
    if (readinessScore >= 50) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const getReadinessLabel = (readinessScore: number) => {
    if (readinessScore >= 90) return 'Ready';
    if (readinessScore >= 70) return 'Almost Ready';
    if (readinessScore >= 50) return 'Needs Config';
    return 'Not Ready';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold">Client not found</h2>
        <p className="mt-2 text-muted-foreground">
          The client you're looking for doesn't exist.
        </p>
        <Link
          to="/clients"
          className="mt-4 inline-block text-primary hover:underline"
        >
          Back to clients
        </Link>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
      case 'suspended':
        return 'bg-gray-100 text-gray-800';
      case 'deploying':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <ClientOverview
            client={client}
            readiness={readiness}
            readinessLoading={readinessLoading}
            latestDeployment={latestDeployment}
            deploymentsLoading={deploymentsLoading}
            deploymentStatus={deploymentStatus}
            loadingDeploymentStatus={loadingDeploymentStatus}
            onProvisionDatabase={handleProvisionDatabase}
            onStartLocalDeployment={handleStartLocalDeployment}
            onStopDeployment={handleStopDeployment}
            provisioningDatabase={provisioningDatabase}
            deploying={deploying}
          />
        );
      
      case 'config':
        return (
          <ClientConfiguration
            clientId={id!}
            configs={configs}
            configsLoading={configsLoading}
            editingKey={editingKey}
            editValue={editValue}
            showSecrets={showSecrets}
            saving={saving}
            onInitializeDefaults={handleInitializeDefaults}
            onSaveConfig={handleSaveConfig}
            onDeleteConfig={handleDeleteConfig}
            onStartEdit={startEdit}
            onCancelEdit={cancelEdit}
            onToggleSecretVisibility={toggleSecretVisibility}
          />
        );
      
      case 'logs':
        return <ClientLogs clientId={id!} />;
      
      case 'users':
        return <ClientUsersTab clientId={id!} clientName={client?.name || ''} />;
      
      case 'anthropic':
        return <ClientAnthropic clientId={id!} />;
      
      case 'billing':
        return <ClientBilling clientId={id!} />;
      
      case 'api_keys':
        return <ClientApiKeys clientId={id!} />;
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link to="/clients" className="hover:text-foreground">
          Clients
        </Link>
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-foreground">{client.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold tracking-tight">{client.name}</h1>
            <span className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(client.status)}`}>
              {client.status}
            </span>
          </div>
          <p className="mt-2 text-muted-foreground">
            {client.subdomain}.byteable.ai
          </p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setActiveTab('config')}
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Configure
          </button>
          {readinessLoading ? (
            <button 
              disabled
              className="rounded-md bg-gray-300 px-4 py-2 text-sm font-medium text-gray-500 cursor-not-allowed"
            >
              Checking...
            </button>
          ) : readiness ? (
            <button 
              onClick={handleDeploy}
              disabled={!readiness.isReady || deploying}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                readiness.isReady && !deploying
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {deploying ? (
                <span className="flex items-center gap-2">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Deploying...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Deploy
                  <span className={`px-2 py-1 text-xs rounded-full ${getReadinessColor(readiness.readinessScore)}`}>
                    {getReadinessLabel(readiness.readinessScore)}
                  </span>
                </span>
              )}
            </button>
          ) : (
            <button 
              disabled
              className="rounded-md bg-gray-300 px-4 py-2 text-sm font-medium text-gray-500 cursor-not-allowed"
            >
              Deploy
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'overview'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'config'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'logs'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Logs
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'users'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab('anthropic')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'anthropic'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Anthropic
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'billing'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Billing
          </button>
          <button
            onClick={() => setActiveTab('api_keys')}
            className={`border-b-2 pb-3 text-sm font-medium transition-colors ${
              activeTab === 'api_keys'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            API Keys
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */